/**
 * Générateur — Lien entre × 0,1/0,01/0,001 et ÷ 10/100/1 000
 * Attendu ID : 611 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - lien_div_01_qcm_unique
 *   - lien_div_01_qcm_multiple
 *   - lien_div_01_relier
 *   - lien_div_01_tableau_vf
 *   - lien_div_01_texte_trou_drag
 *
 * Principe : l'équivalence × 0,1 ≡ ÷ 10, × 0,01 ≡ ÷ 100, × 0,001 ≡ ÷ 1 000
 * est une compétence CONCEPTUELLE. Les exercices sont qualitatifs — on ne
 * demande JAMAIS de calculer le résultat numérique (c'est l'attendu 610).
 * L'élève doit reconnaître l'équivalence × ↔ ÷.
 *
 * Pièges couverts (3 familles cognitives distinctes) :
 *   - Sens inversé / surgénéralisation "plus grand" (× 10 au lieu de ÷ 10)
 *   - Confusion du nombre de zéros (÷ 10 au lieu de ÷ 100)
 *   - Division par un décimal (÷ 0,1 — hors programme, piège "symétrie visuelle")
 *
 * Variantes estimées : > 1500
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 611;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Les 3 constantes du cluster, avec leur diviseur équivalent. */
const CONSTANTES = [
    { str: '0,1',   k: 1, divStr: '10',    multStr: '10'   },
    { str: '0,01',  k: 2, divStr: '100',   multStr: '100'  },
    { str: '0,001', k: 3, divStr: '1 000', multStr: '1 000' },
];

// ─── Labels ───
const L_QCM = 'Identifier la division équivalente à × 0,1 / 0,01 / 0,001';
const L_QCM_MULTI = 'Identifier les équivalences × ↔ ÷ correctes';
const L_RELIER = 'Associer une multiplication par 0,1/0,01/0,001 à sa division équivalente';
const L_VF = 'Vrai/Faux sur les équivalences × ↔ ÷';
const L_TROU_DRAG = 'Compléter des équivalences × / ÷ avec les bonnes divisions';
const L_SENS = 'Comprendre le sens de la division par 10/100/1000 (déplacement de virgule) et son équivalence en × 0,1/0,01/0,001';

// ─── Pool de facteurs libres (entiers et décimaux simples) ───
// On évite des valeurs trop "grosses" : l'intérêt est qualitatif, pas arithmétique.
const POOL_FACTEURS = [
    '3', '5', '7', '8', '9', '12', '15', '18', '25', '32',
    '45', '50', '60', '75', '80', '100', '120', '150', '200', '240',
    '320', '500', '600', '750', '800', '1 000', '1 200', '2 500',
    '1,5', '2,4', '3,14', '4,8', '6,5', '7,8', '9,5', '12,5',
];

const randFacteur = () => randomChoice(POOL_FACTEURS);

/** Retourne un entier 0/1/2 ≠ k. */
const randOtherIdx = (k) => {
    const others = [0, 1, 2].filter((x) => x !== k);
    return others[Math.floor(Math.random() * others.length)];
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Par quelle opération peut-on remplacer × c ?
// ═══════════════════════════════════════════════════════════

const FORMULATIONS_QCM_UNIQUE = [
    (c) => `<p>Par quelle opération peut-on remplacer « × ${c} » pour obtenir le même résultat ?</p>`,
    (c) => `<p>« × ${c} » est équivalent à quelle opération ?</p>`,
    (c) => `<p>Multiplier par ${c} revient à faire quelle opération ?</p>`,
    (c, a) => `<p>Par quoi peut-on remplacer « × ${c} » dans ${a} × ${c} ?</p>`,
];

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureQcm611 = null;

const genLienDivQcm = () => {
    let payload = construireLienDivQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm611; t++) {
        payload = construireLienDivQcm();
    }
    derniereSignatureQcm611 = payload.texte;
    return payload;
};

const construireLienDivQcm = () => {
    const idx = randInt(0, 2);
    const constante = CONSTANTES[idx];
    const correct = `÷ ${constante.divStr}`;

    // Choix APPARIÉS (revue 2026-08-19) : l'ancien lot {3 ÷, × N} portait le
    // nombre de la bonne réponse en double (× N miroir, 100 % des tirages) —
    // l'élève trouvait ÷ N par appariement sans compter les zéros. Désormais
    // chaque nombre apparaît une fois en ÷ et une fois en × : il faut choisir
    // le SENS et compter les ZÉROS.
    const voisin = CONSTANTES[randOtherIdx(idx)];
    const distracteurs = [
        `× ${constante.divStr}`,   // sens inversé (erreur classique conservée)
        `÷ ${voisin.divStr}`,      // confusion de zéros
        `× ${voisin.divStr}`,      // les deux erreurs combinées
    ];

    // Formulation : 60% avec facteur libre (revue 2026-08-19 : les 9 phrases
    // fixes concentraient 75 % des tirages — 298 répétitions immédiates).
    const withA = Math.random() < 0.6;
    let texte;
    if (withA) {
        const a = randFacteur();
        texte = FORMULATIONS_QCM_UNIQUE[3](constante.str, a);
    } else {
        const formul = randomChoice(FORMULATIONS_QCM_UNIQUE.slice(0, 3));
        texte = formul(constante.str);
    }

    return makeQcm(
        [correct, ...distracteurs],
        correct,
        texte,
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Parmi ces équivalences, lesquelles sont vraies ?
// ═══════════════════════════════════════════════════════════

const genLienDivQcmMultiple = () => {
    const propositions = []; // { text, isVrai }
    const used = new Set();

    const addProp = (text, isVrai) => {
        if (used.has(text)) return false;
        used.add(text);
        propositions.push({ text, isVrai });
        return true;
    };

    // 3 VRAIES — une par constante (couverture exhaustive)
    const vraisIdx = shuffle([0, 1, 2]);
    for (const idx of vraisIdx) {
        for (let tries = 0; tries < 20; tries++) {
            const a = randFacteur();
            const c = CONSTANTES[idx];
            if (addProp(`${a} × ${c.str} = ${a} ÷ ${c.divStr}`, true)) break;
        }
    }

    // 3 FAUSSES — 3 familles de pièges cognitifs distinctes :
    //  (F1) Confusion du nombre de zéros (÷ k' avec k' ≠ k)
    //  (F2) Sens inversé (× divStr au lieu de ÷ divStr)
    //  (F3) Confusion × / × (remplace × c par × k — lit la constante comme 10^k)
    //        ou division par un décimal (÷ c au lieu de ÷ k)

    // (F1) Confusion de zéros
    for (let tries = 0; tries < 30; tries++) {
        const a = randFacteur();
        const idx = randInt(0, 2);
        const wrongIdx = randOtherIdx(idx);
        const c = CONSTANTES[idx];
        const wrong = CONSTANTES[wrongIdx];
        if (addProp(`${a} × ${c.str} = ${a} ÷ ${wrong.divStr}`, false)) break;
    }

    // (F2) Sens inversé
    for (let tries = 0; tries < 30; tries++) {
        const a = randFacteur();
        const c = randomChoice(CONSTANTES);
        if (addProp(`${a} × ${c.str} = ${a} × ${c.multStr}`, false)) break;
    }

    // (F3) Division par un décimal — piège "symétrie visuelle" ÷ c
    for (let tries = 0; tries < 30; tries++) {
        const a = randFacteur();
        const c = randomChoice(CONSTANTES);
        if (addProp(`${a} × ${c.str} = ${a} ÷ ${c.str}`, false)) break;
    }

    // Revue 2026-08-23 (choix prof) : 4 propositions au lieu de 6 — on retire
    // d'abord deux propositions au hasard (fausses en priorité), les 3
    // familles restent en rotation d'un tirage à l'autre.
    const CIBLE_PROPS = 4;
    for (let r = 0; r < 2; r++) {
        const fausses = propositions.filter((p) => !p.isVrai);
        const retiree = fausses.length ? randomChoice(fausses) : randomChoice(propositions);
        propositions.splice(propositions.indexOf(retiree), 1);
    }

    // Quota VARIABLE de vraies (revue 2026-08-19, borné à la nouvelle cible) :
    // 1 à 3 vraies sur 4 propositions.
    const nbVrai = randInt(1, 3);
    const ajouterVraie = () => {
        for (let t = 0; t < 30; t++) {
            const a = randFacteur();
            const c = randomChoice(CONSTANTES);
            if (addProp(`${a} × ${c.str} = ${a} ÷ ${c.divStr}`, true)) return true;
        }
        return false;
    };
    const ajouterFausse = () => {
        for (let t = 0; t < 30; t++) {
            const a = randFacteur();
            const idx = randInt(0, 2);
            const wrong = CONSTANTES[randOtherIdx(idx)];
            if (addProp(`${a} × ${CONSTANTES[idx].str} = ${a} ÷ ${wrong.divStr}`, false)) return true;
        }
        return false;
    };
    for (let garde = 0; garde < 10; garde++) {
        const vraies = propositions.filter((p) => p.isVrai);
        const fausses = propositions.filter((p) => !p.isVrai);
        if (propositions.length < CIBLE_PROPS) {
            if (!(vraies.length < nbVrai ? ajouterVraie() : ajouterFausse())) break;
        } else if (vraies.length > nbVrai) {
            propositions.splice(propositions.indexOf(randomChoice(vraies)), 1);
            if (!ajouterFausse()) break;
        } else if (vraies.length < nbVrai) {
            propositions.splice(propositions.indexOf(randomChoice(fausses)), 1);
            if (!ajouterVraie()) break;
        } else break;
    }

    const finalProps = shuffle(propositions).slice(0, CIBLE_PROPS);
    const allTexts = finalProps.map((p) => p.text);
    const correctTexts = finalProps.filter((p) => p.isVrai).map((p) => p.text);

    return makeQcm(
        allTexts,
        correctTexts,
        `<p>Parmi les égalités suivantes, lesquelles sont vraies ?</p>`,
        opts(L_QCM_MULTI),
        true,
    );
};

// ═══════════════════════════════════════════════════════════
// 3. RELIER — Associer × c ↔ ÷ k
// ═══════════════════════════════════════════════════════════

const genLienDivRelier = () => {
    const pairs = [];
    const usedSources = new Set();
    const usedTargets = new Set();

    const tryAdd = (a, c) => {
        const source = `${a} × ${c.str}`;
        const target = `${a} ÷ ${c.divStr}`;
        if (usedSources.has(source) || usedTargets.has(target)) return false;
        usedSources.add(source);
        usedTargets.add(target);
        pairs.push({ source, target });
        return true;
    };

    for (let attempt = 0; attempt < 50; attempt++) {
        pairs.length = 0;
        usedSources.clear();
        usedTargets.clear();

        // Heuristique "discrimination rang-par-rang" :
        // On prend un facteur `aShared` utilisé avec 2 constantes différentes
        // (l'élève qui confond ÷ 10 et ÷ 100 mélangera ces 2 paires).
        const aShared = randFacteur();
        const cIdx1 = randInt(0, 2);
        const cIdx2 = randOtherIdx(cIdx1);
        tryAdd(aShared, CONSTANTES[cIdx1]);
        tryAdd(aShared, CONSTANTES[cIdx2]);

        // 2 paires "de contrôle" avec d'autres facteurs (3ème constante + une autre)
        const cIdx3 = [0, 1, 2].find((x) => x !== cIdx1 && x !== cIdx2);
        for (let t = 0; t < 20; t++) {
            const aOther = randFacteur();
            if (aOther === aShared) continue;
            if (tryAdd(aOther, CONSTANTES[cIdx3])) break;
        }
        for (let t = 0; t < 20; t++) {
            const aOther = randFacteur();
            if (aOther === aShared) continue;
            if (tryAdd(aOther, CONSTANTES[randInt(0, 2)])) break;
        }

        if (pairs.length === 4 && usedTargets.size === 4) break;
    }

    // Fallback déterministe si boucle épuisée
    if (pairs.length < 4 || usedTargets.size < 4) {
        pairs.length = 0;
        usedSources.clear();
        usedTargets.clear();
        tryAdd('45',  CONSTANTES[0]);   // 45 × 0,1  ↔ 45 ÷ 10
        tryAdd('45',  CONSTANTES[1]);   // 45 × 0,01 ↔ 45 ÷ 100
        tryAdd('8',   CONSTANTES[2]);   // 8 × 0,001 ↔ 8 ÷ 1 000
        tryAdd('320', CONSTANTES[1]);   // 320 × 0,01 ↔ 320 ÷ 100
    }

    return makeRelier(
        pairs,
        `<p>Relie chaque multiplication à la division qui donne le même résultat.</p>`,
        opts(L_RELIER),
    );
};

// ═══════════════════════════════════════════════════════════
// 4. TABLEAU VRAI/FAUX — 6 équivalences, 3V/3F
// ═══════════════════════════════════════════════════════════

const genLienDivVF = () => {
    const lignes = []; // { text, isVrai }
    const used = new Set();

    const addLigne = (text, isVrai) => {
        if (used.has(text)) return false;
        used.add(text);
        lignes.push({ text, isVrai });
        return true;
    };

    // 3 VRAIES — une par constante
    const idxs = shuffle([0, 1, 2]);
    for (const idx of idxs) {
        for (let t = 0; t < 20; t++) {
            const a = randFacteur();
            const c = CONSTANTES[idx];
            if (addLigne(`${a} × ${c.str} = ${a} ÷ ${c.divStr}`, true)) break;
        }
    }

    // 3 FAUSSES — 3 pièges distincts
    // (F1) Confusion des zéros
    for (let t = 0; t < 30; t++) {
        const a = randFacteur();
        const idx = randInt(0, 2);
        const wrongIdx = randOtherIdx(idx);
        const c = CONSTANTES[idx];
        const wrong = CONSTANTES[wrongIdx];
        if (addLigne(`${a} × ${c.str} = ${a} ÷ ${wrong.divStr}`, false)) break;
    }

    // (F2) Division par un décimal (hors programme, piège "symétrie")
    for (let t = 0; t < 30; t++) {
        const a = randFacteur();
        const c = randomChoice(CONSTANTES);
        if (addLigne(`${a} × ${c.str} = ${a} ÷ ${c.str}`, false)) break;
    }

    // (F3) Sens inversé : × c = × divStr
    for (let t = 0; t < 30; t++) {
        const a = randFacteur();
        const c = randomChoice(CONSTANTES);
        if (addLigne(`${a} × ${c.str} = ${a} × ${c.multStr}`, false)) break;
    }

    // Quota VARIABLE de vraies (revue 2026-08-20) : le 3V/3F constant était
    // comptable — l'élève finissait le tableau par déduction. On vise 2 à 4.
    const nbVrai = randInt(2, 4);
    const ajouterVraie = () => {
        for (let t = 0; t < 30; t++) {
            const a = randFacteur();
            const c = randomChoice(CONSTANTES);
            if (addLigne(`${a} × ${c.str} = ${a} ÷ ${c.divStr}`, true)) return true;
        }
        return false;
    };
    const ajouterFausse = () => {
        for (let t = 0; t < 30; t++) {
            const a = randFacteur();
            const idx = randInt(0, 2);
            const wrong = CONSTANTES[randOtherIdx(idx)];
            if (addLigne(`${a} × ${CONSTANTES[idx].str} = ${a} ÷ ${wrong.divStr}`, false)) return true;
        }
        return false;
    };
    for (let garde = 0; garde < 10; garde++) {
        const vraies = lignes.filter((l) => l.isVrai);
        const fausses = lignes.filter((l) => !l.isVrai);
        if (lignes.length < 6) {
            if (!(vraies.length < nbVrai ? ajouterVraie() : ajouterFausse())) break;
        } else if (vraies.length > nbVrai) {
            lignes.splice(lignes.indexOf(randomChoice(vraies)), 1);
            if (!ajouterFausse()) break;
        } else if (vraies.length < nbVrai) {
            lignes.splice(lignes.indexOf(randomChoice(fausses)), 1);
            if (!ajouterVraie()) break;
        } else break;
    }

    const finalLignes = shuffle(lignes).slice(0, 6);
    const lignesTexts = finalLignes.map((l) => l.text);
    // 0 = Vrai, 1 = Faux (convention utils.js)
    const bonnesReponses = finalLignes.map((l) => (l.isVrai ? 0 : 1));

    return makeVraiFaux(
        lignesTexts,
        bonnesReponses,
        `<p>Pour chaque égalité, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. TEXTE À TROUS (DRAG) — 3 trous, divisions équivalentes
// ═══════════════════════════════════════════════════════════

const genLienDivTrouDrag = () => {
    // 3 trous, un par constante (couverture exhaustive des 3 équivalences)
    const idxOrder = shuffle([0, 1, 2]);
    const parts = [];
    const trous = new Set();
    const usedA = new Set();

    for (const idx of idxOrder) {
        const c = CONSTANTES[idx];
        let a;
        for (let t = 0; t < 30; t++) {
            a = randFacteur();
            if (!usedA.has(a)) break;
        }
        usedA.add(a);
        const trou = `÷ ${c.divStr}`;
        trous.add(trou);
        parts.push(`${a} × ${c.str} = ${a} {{${trou}}}`);
    }

    const phrase = parts.join('\n');

    // Distracteurs : 3 familles cognitives distinctes
    // (1) Sens inversé / surgénéralisation : × 10
    // (2) Division par un décimal : ÷ 0,1  (piège symétrie visuelle)
    // (3) Confusion d'opération : + 100 (division ↔ addition)
    const distracteursPool = [
        { val: '× 10',   famille: 'sens_inverse' },
        { val: '× 100',  famille: 'sens_inverse' },
        { val: '÷ 0,1',  famille: 'div_decimal' },
        { val: '÷ 0,01', famille: 'div_decimal' },
        { val: '+ 100',  famille: 'op_confusion' },
        { val: '+ 10',   famille: 'op_confusion' },
    ];

    const picked = [];
    const pickedFamilles = new Set();
    const shuffled = shuffle(distracteursPool);
    for (const d of shuffled) {
        if (pickedFamilles.has(d.famille)) continue;
        if (trous.has(d.val)) continue;
        picked.push(d.val);
        pickedFamilles.add(d.famille);
        if (picked.length === 3) break;
    }
    while (picked.length < 3) {
        for (const d of shuffled) {
            if (picked.includes(d.val) || trous.has(d.val)) continue;
            picked.push(d.val);
            if (picked.length === 3) break;
        }
        if (picked.length >= 3) break;
        break;
    }

    return makeTexteTrou(
        phrase,
        `<p>Complète chaque égalité en faisant glisser l'opération équivalente.</p>`,
        { ...opts(L_TROU_DRAG), mode: 'drag', strict: false, distracteurs: picked },
    );
};

// ═══════════════════════════════════════════════════════════
// 6. SENS & DÉPLACEMENT — Texte à trous conceptuel
// ═══════════════════════════════════════════════════════════
//
// Template inspiré du manuel : on donne le nombre et son chiffre des unités
// (pour que l'élève n'ait plus à le chercher), puis on lui fait compléter
// une phrase qui articule 3 niveaux de compréhension :
//   1. Sens           → "X fois plus petit"
//   2. Mécanisme      → "le chiffre des unités devient le chiffre des dixièmes"
//   3. Lien × ↔ ÷     → "diviser par 10 = multiplier par 0,1"
//
// Format : texte à trous (saisie) avec 5 blancs.

// Décale la virgule vers la gauche de k rangs (identique à gen_610.shiftLeft).
// Manipulation string pour éviter les erreurs IEEE 754.
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    const totalLen = allDigits.length;

    if (newDecs < totalLen) {
        const newInt = allDigits.slice(0, totalLen - newDecs);
        const newDec = allDigits.slice(totalLen - newDecs);
        const newDecTrim = newDec.replace(/0+$/, '');
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(newDecs - totalLen);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    if (decTrim === '') return '0';
    return `0,${decTrim}`;
};

/** Retourne le chiffre des unités d'un nombre en format FR. */
const getUnitDigit = (xStr) => {
    const clean = String(xStr).replace(/\s/g, '');
    const [intPart] = clean.split(',');
    return intPart[intPart.length - 1];
};

// Pool filtré : on exclut les nombres dont le chiffre des unités est 0
// (comme 100, 240, 1 000...) car le "chiffre des unités" serait 0 et
// le résultat après division ne montre pas clairement ce 0 déplacé.
const POOL_SENS = [
    '3', '5', '7', '8', '9', '12', '15', '18', '25', '32', '45', '75',
    '1,5', '2,4', '3,14', '4,8', '6,5', '7,8', '9,5', '12,5',
];

const POSITIONS_BY_K = { 1: 'dixièmes', 2: 'centièmes', 3: 'millièmes' };
const CONSTANTES_BY_K = { 1: '0,1', 2: '0,01', 3: '0,001' };
const DIVISEURS_BY_K = { 1: '10', 2: '100', 3: '1 000' };

// Anti-répétition immédiate (revue 2026-08-20, recette standard). Le trou
// « diviseur » copié de l'énoncé est CONSERVÉ (décision utilisateur).
let derniereSignatureSens611 = null;

const genLienDivSensTrouSaisie = () => {
    let payload = construireLienDivSensTrouSaisie();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureSens611; t++) {
        payload = construireLienDivSensTrouSaisie();
    }
    derniereSignatureSens611 = payload.contenu.phrase;
    return payload;
};

const construireLienDivSensTrouSaisie = () => {
    const X = randomChoice(POOL_SENS);
    const k = randInt(1, 3);
    const diviseur = DIVISEURS_BY_K[k];
    const position = POSITIONS_BY_K[k];
    const constante = CONSTANTES_BY_K[k];
    const resultat = shiftLeft(X, k);
    const unitDigit = getUnitDigit(X);

    // Énoncé : donne le nombre et son chiffre des unités (pré-mâché pour
    // que l'élève se concentre sur le déplacement, pas la reconnaissance)
    const texte = `<p>Le nombre est <strong>${X}</strong>. Le chiffre des unités est <strong>${unitDigit}</strong>.</p><p>Complète les phrases ci-dessous :</p>`;

    // 5 blancs : diviseur / petit / position / résultat / constante équivalente
    const phrase =
        `Diviser ${X} par ${diviseur}, c'est trouver le nombre {{${diviseur}}} fois plus {{petit}} que ${X}.\n` +
        `Le chiffre des unités de ${X} devient alors le chiffre des {{${position}}} et donc ${X} ÷ ${diviseur} = {{${resultat}}}.\n` +
        `Donc ${X} × {{${constante}}} = ${resultat}.`;

    return makeTexteTrou(phrase, texte, {
        ...opts(L_SENS),
        mode: 'saisie',
        strict: false,
        distracteurs: [],
    });
};

// ─── Registre ───
const GENERATORS = {
    lien_div_01_qcm_unique: genLienDivQcm,
    lien_div_01_qcm_multiple: genLienDivQcmMultiple,
    lien_div_01_relier: genLienDivRelier,
    lien_div_01_tableau_vf: genLienDivVF,
    lien_div_01_texte_trou_drag: genLienDivTrouDrag,
    lien_div_01_texte_trou_saisie: genLienDivSensTrouSaisie,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lien_div_01', label: 'Lien × 0,1/0,01/0,001 ↔ ÷ 10/100/1 000', formatsList: buildFormats('lien_div_01') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
